import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useToast } from './ToastContext';
import rateLimiter from '../utils/rateLimiter';
import { handleApiError } from '../services/errorHandler.js';
import { ErrorCodes, ErrorCategory } from '../types/errors.js';
import { errorHandler } from '../services/errorHandler.js';

// Supabase Auth types
export interface User {
  id: string;
  email: string;
  email_confirmed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  showPasswordRecoveryForm: boolean;
  setShowPasswordRecoveryForm: (show: boolean) => void;
  isInRecoveryMode: boolean;
  setIsInRecoveryMode: (mode: boolean) => void;
  showAuthModal: boolean;
  authModalMode: 'signin' | 'signup';
  authModalError: string | null;
  setShowAuthModal: (show: boolean, mode?: 'signin' | 'signup', error?: string) => void;
  signUp: (email: string, password: string) => Promise<{ error?: string; needsConfirmation?: boolean; confirmationEmail?: string }>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error?: string; needsConfirmation?: boolean; confirmationEmail?: string }>;
  signInWithGoogle: (rememberMe?: boolean) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  resendConfirmation: (email: string) => Promise<{ error?: string }>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updatePasswordWithRecovery: (newPassword: string) => Promise<void>;
  deleteAccount: (currentPassword: string) => Promise<void>;
  checkSession: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshSession: () => Promise<boolean>;
  cancelPasswordRecovery: () => Promise<void>;
  // Development/testing utilities
  forceExpireSession?: () => void;
  getSessionInfo?: () => { expiresAt: number; timeLeft: number; storage: 'localStorage' | 'sessionStorage' | null };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordRecoveryForm, setShowPasswordRecoveryForm] = useState(false);
  const [isInRecoveryMode, setIsInRecoveryMode] = useState(false);
  const [showAuthModal, setShowAuthModalState] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'signin' | 'signup'>('signin');
  const [authModalError, setAuthModalError] = useState<string | null>(null);
  const hasRunProactiveRefresh = useRef(false);
  const { showSuccess, showInfo } = useToast();

  // Development settings
  const isDevelopment = import.meta.env.DEV;
  const DEV_SESSION_EXPIRY_MINUTES = 2; // 2 minutes for testing

  // Helper function to calculate session expiry time
  const getSessionExpiryTime = (expiresInSeconds: number): number => {
    if (isDevelopment) {
      // In development, use much shorter expiry for testing
      return Date.now() + (DEV_SESSION_EXPIRY_MINUTES * 60 * 1000);
    }
    return Date.now() + (expiresInSeconds * 1000);
  };

  const API_BASE = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';
  const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxvY2FsaG9zdCIsInJvbGUiOiJhbm9uIiwiaWF0IjoxNzM2NzA1NjAwLCJleHAiOjE4NTIzNjI3MDB9.a2kcrdv2Ydk4yHxB4HTkXUdFna-nt4r-OgQW-Tq8a_k';

  // Helper function to get appropriate error code for auth operations
  const getAuthErrorCode = (_operation: string, response?: Response, errorMessage?: string): string => {
    if (response?.status === 401) return ErrorCodes.API_UNAUTHORIZED;
    if (response?.status === 403) return ErrorCodes.API_FORBIDDEN; 
    if (response?.status === 429) return ErrorCodes.API_RATE_LIMIT;
    if (response?.status === 422) return ErrorCodes.VALIDATION_INVALID_EMAIL;
    
    const lowerMessage = errorMessage?.toLowerCase() || '';
    if (lowerMessage.includes('password') && lowerMessage.includes('weak')) return ErrorCodes.VALIDATION_PASSWORD_WEAK;
    if (lowerMessage.includes('email')) return ErrorCodes.VALIDATION_INVALID_EMAIL;
    if (lowerMessage.includes('credentials')) return ErrorCodes.AUTH_INVALID_CREDENTIALS;
    if (lowerMessage.includes('expired')) return ErrorCodes.AUTH_EXPIRED;
    if (lowerMessage.includes('confirmation') || lowerMessage.includes('confirmed')) return ErrorCodes.AUTH_EMAIL_NOT_CONFIRMED;
    if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many')) return ErrorCodes.API_RATE_LIMIT;
    
    return ErrorCodes.API_SERVER_ERROR;
  };

  const checkSession = useCallback(async () => {
    try {
      // First check if we're in recovery mode
      const recoverySession = localStorage.getItem('cycling-buddy-recovery-session');
      if (recoverySession) {
        // User is in recovery mode - don't allow normal authentication
        setIsInRecoveryMode(true);
        setShowPasswordRecoveryForm(true);
        setUser(null); // Don't authenticate until password is reset
        setLoading(false);
        return;
      }

      // Check both localStorage and sessionStorage for existing sessions
      let storedSession = localStorage.getItem('cycling-buddy-session');
      let storage = localStorage;
      
      if (!storedSession) {
        storedSession = sessionStorage.getItem('cycling-buddy-session');
        storage = sessionStorage;
      }
      
      if (storedSession) {
        const session = JSON.parse(storedSession);
        if (session.access_token && session.expires_at > Date.now()) {
          // Handle both JWT tokens and session-based authentication
          if (session.access_token === 'supabase-session-based') {
            // Session-based authentication - verify via user endpoint without JWT
            const response = await fetch(`${API_BASE}/auth/v1/user`, {
              headers: {
                'apikey': ANON_KEY
              },
              credentials: 'include' // Include cookies for session-based auth
            });

            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
            } else {
              // Session invalid, clear it from the appropriate storage
              storage.removeItem('cycling-buddy-session');
            }
          } else {
            // Traditional JWT token authentication
            const response = await fetch(`${API_BASE}/auth/v1/user`, {
              headers: {
                'apikey': ANON_KEY,
                'Authorization': `Bearer ${session.access_token}`
              }
            });

            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
            } else {
              // Session invalid, clear it from the appropriate storage
              storage.removeItem('cycling-buddy-session');
            }
          }
        } else {
          // Session expired, clear it from the appropriate storage
          storage.removeItem('cycling-buddy-session');
        }
      }
    } catch (error) {
      console.error('Error checking session:', error);
      
      // Log to centralized error handling system
      await handleApiError(error, '/auth/v1/user', 'check_session');
      
      localStorage.removeItem('cycling-buddy-session');
      sessionStorage.removeItem('cycling-buddy-session');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, ANON_KEY]);

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Update error handler with user ID when user changes
  useEffect(() => {
    errorHandler.setUserId(user?.id || undefined);
    
    // Reset proactive refresh flag when user changes
    if (!user) {
      hasRunProactiveRefresh.current = false;
    }
  }, [user?.id]);

  const signUp = async (email: string, password: string): Promise<{ error?: string; needsConfirmation?: boolean; confirmationEmail?: string }> => {
    const rateLimitKey = `signup_${email}`;
    
    // Check rate limiting
    const rateLimitCheck = rateLimiter.isAllowed(rateLimitKey, 'signup');
    if (!rateLimitCheck.allowed) {
      return { error: rateLimitCheck.error };
    }

    try {

      const response = await fetch(`${API_BASE}/auth/v1/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': ANON_KEY
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Parse specific error messages from Supabase first
        const errorMessage = data.msg || data.message || data.error_description || 'Sign up failed';
        
        // Record failed attempt for rate limiting
        rateLimiter.recordAttempt(rateLimitKey, 'signup', false);
        
        // Log to centralized error handling system with proper categorization
        await handleApiError({
          message: errorMessage,
          status: response.status,
          statusText: response.statusText
        }, '/auth/v1/signup', 'user_signup', { 
          email,
          error_code: getAuthErrorCode('signup', response, errorMessage),
          category: ErrorCategory.AUTHENTICATION
        });
        
        // Handle specific error cases for better UX
        if (errorMessage.toLowerCase().includes('user already registered') || 
            errorMessage.toLowerCase().includes('email already registered') ||
            errorMessage.toLowerCase().includes('user already exists') ||
            errorMessage.toLowerCase().includes('already been registered')) {
          return { 
            error: 'An account with this email address already exists. Please sign in instead or use the "Forgot Password" option.' 
          };
        }
        
        if (errorMessage.toLowerCase().includes('password') && errorMessage.toLowerCase().includes('weak')) {
          return { 
            error: 'Please choose a stronger password with at least 8 characters, including uppercase, lowercase, and numbers.' 
          };
        }
        
        if (errorMessage.toLowerCase().includes('invalid email')) {
          return { 
            error: 'Please enter a valid email address.' 
          };
        }
        
        // Handle rate limiting (429) as potential duplicate signup attempt
        if (response.status === 429 || errorMessage.toLowerCase().includes('rate limit')) {
          return { 
            error: 'You recently tried to sign up with this email. If you already have an account, please sign in instead. Otherwise, please wait a few minutes before trying again.' 
          };
        }
        
        if (errorMessage.toLowerCase().includes('too many requests')) {
          return { 
            error: 'Too many signup attempts. Please wait a few minutes before trying again.' 
          };
        }
        
        // Return the original error message if no specific case matches
        return { error: errorMessage };
      }

      // CRITICAL: Check for duplicate signup attempts
      const userData = data.user || data;
      
      // Case 1: Fake user response for confirmed existing users
      // Characteristics: empty identities array, no session, no access_token
      if (userData && !data.session && !data.access_token && 
          userData.identities && userData.identities.length === 0) {
        
        return { 
          error: 'An account with this email address may already exist. Please try signing in instead or use the "Forgot Password" option if you need to reset your password.' 
        };
      }
      
      // Case 2: Duplicate signup for unconfirmed existing user
      // Characteristics: confirmation_sent_at is significantly newer than created_at
      if (userData && userData.created_at && userData.confirmation_sent_at) {
        const createdTime = new Date(userData.created_at).getTime();
        const confirmationTime = new Date(userData.confirmation_sent_at).getTime();
        const timeDifference = confirmationTime - createdTime;
        
        // If confirmation was sent more than 30 seconds after account creation,
        // this is likely a duplicate signup attempt for an unconfirmed account
        if (timeDifference > 30000) { // 30 seconds threshold
 
          return { 
            needsConfirmation: true,
            confirmationEmail: userData.email
          };
        }
      }

      // Store session if provided
      if (data.access_token) {
        const session = {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: getSessionExpiryTime(data.expires_in)
        };
        
        // Store in localStorage for signup - new users typically expect persistent sessions
        localStorage.setItem('cycling-buddy-session', JSON.stringify(session));
        
        setUser(data.user);
        
        // Record successful attempt to reset rate limiting
        rateLimiter.recordAttempt(rateLimitKey, 'signup', true);
        
        // Only show success toast for immediate signup (when user gets logged in right away)
        // When email confirmation is needed, AuthModal will handle the messaging with success modal
        showSuccess('Account created successfully! Welcome to CyclingBuddy');
      } else {
        // No access token - likely needs email confirmation
        // Still record as successful since signup worked
        rateLimiter.recordAttempt(rateLimitKey, 'signup', true);
        // Don't show toast here - AuthModal will show the success modal with email confirmation message
      }

      return {};
    } catch (error) {
      console.error('Sign up error:', error);
      // Record failed attempt for network errors too
      rateLimiter.recordAttempt(rateLimitKey, 'signup', false);
      
      // Log to centralized error handling system
      await handleApiError(error, '/auth/v1/signup', 'user_signup', { email });
      
      return { error: 'Network error during sign up. Please check your connection and try again.' };
    }
  };

  // Helper function to check if a user exists by trying to resend confirmation
  const signIn = async (email: string, password: string, rememberMe: boolean = true): Promise<{ error?: string; needsConfirmation?: boolean; confirmationEmail?: string }> => {
    const rateLimitKey = `signin_${email}`;
    
    // Check rate limiting
    const rateLimitCheck = rateLimiter.isAllowed(rateLimitKey, 'signin');
    if (!rateLimitCheck.allowed) {
      return { error: rateLimitCheck.error };
    }

    try {
      const response = await fetch(`${API_BASE}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': ANON_KEY
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Parse specific error messages from Supabase first
        const errorMessage = data.msg || data.message || data.error_description || 'Sign in failed';
        
        // Record failed attempt for rate limiting
        rateLimiter.recordAttempt(rateLimitKey, 'signin', false);
        
        // Log to centralized error handling system with proper categorization
        await handleApiError({
          message: errorMessage,
          status: response.status,
          statusText: response.statusText
        }, '/auth/v1/token', 'user_signin', { 
          email,
          error_code: getAuthErrorCode('signin', response, errorMessage),
          category: ErrorCategory.AUTHENTICATION
        });
        
        // Handle specific error cases for better UX
        if (errorMessage.toLowerCase().includes('invalid login credentials') || 
            errorMessage.toLowerCase().includes('invalid email or password') ||
            errorMessage.toLowerCase().includes('invalid credentials') ||
            response.status === 400) {
          
          return { 
            error: 'Invalid login credentials. Please check your email and password and try again.' 
          };
        }
        
        if (errorMessage.toLowerCase().includes('email not confirmed')) {
          return { 
            error: 'Please check your email and click the confirmation link before signing in.' 
          };
        }
        
        if (errorMessage.toLowerCase().includes('too many requests')) {
          return { 
            error: 'Too many login attempts. Please wait a few minutes before trying again.' 
          };
        }
        
        // Return the original error message if no specific case matches
        return { error: errorMessage };
      }

      // Store session
      const session = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_at: getSessionExpiryTime(data.expires_in)
      };
      
      // Store in localStorage for persistent sessions, sessionStorage for temporary sessions
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('cycling-buddy-session', JSON.stringify(session));
      
      setUser(data.user);
      
      // Record successful attempt to reset rate limiting
      rateLimiter.recordAttempt(rateLimitKey, 'signin', true);
      
      // Show login success message
      showSuccess('Welcome back! You have been logged in successfully');

      return {};
    } catch (error) {
      console.error('Sign in error:', error);
      // Record failed attempt for network errors too
      rateLimiter.recordAttempt(rateLimitKey, 'signin', false);
      
      // Log to centralized error handling system
      await handleApiError(error, '/auth/v1/token', 'user_signin', { email });
      
      return { error: 'Network error during sign in. Please check your connection and try again.' };
    }
  };

  const signOut = async () => {
    try {
      // Check both storages for session
      let storedSession = localStorage.getItem('cycling-buddy-session');
      if (!storedSession) {
        storedSession = sessionStorage.getItem('cycling-buddy-session');
      }
      
      if (storedSession) {
        const session = JSON.parse(storedSession);
        
        // Try to call logout endpoint, but don't fail if it returns 403
        // (which happens when the user has been deleted)
        try {
          const response = await fetch(`${API_BASE}/auth/v1/logout`, {
            method: 'POST',
            headers: {
              'apikey': ANON_KEY,
              'Authorization': `Bearer ${session.access_token}`
            }
          });
          
          // Only log non-403 errors (403 is expected for deleted users)
          if (!response.ok && response.status !== 403) {
            console.warn('Logout endpoint returned non-OK status:', response.status);
          }
        } catch (logoutError) {
          // Silently handle logout errors - the important thing is clearing local state
          console.debug('Logout endpoint error (expected for deleted users):', logoutError);
        }
      }
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      // Always clear local state regardless of logout endpoint success
      localStorage.removeItem('cycling-buddy-session');
      sessionStorage.removeItem('cycling-buddy-session');
      sessionStorage.clear(); // Clear all session storage
      setUser(null);
      
      // Show logout success message
      showInfo('You have been logged out successfully');
      
      // Redirect to root path
      window.location.href = '/';
    }
  };

  // Silent sign out for when user has been deleted (skips logout endpoint and toast)
  const signOutSilent = () => {
    localStorage.removeItem('cycling-buddy-session');
    sessionStorage.removeItem('cycling-buddy-session');
    sessionStorage.clear(); // Clear all session storage
    setUser(null);
    // No toast message for silent logout (used after account deletion)
    
    // Redirect to root path
    window.location.href = '/';
  };

  const signInWithGoogle = async (rememberMe: boolean = true): Promise<{ error?: string }> => {
    try {
      // Store the rememberMe preference for use in the callback
      localStorage.setItem('cycling-buddy-remember-me', JSON.stringify(rememberMe));
      
      // Store the current page URL to redirect back after auth
      localStorage.setItem('cycling-buddy-auth-redirect', window.location.pathname);
      
      // Use Supabase OAuth with proper redirect configuration
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      // Build Supabase OAuth URL with Google provider
      const supabaseOAuthUrl = `${API_BASE}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(redirectUrl)}`;
      
      // Redirect to Supabase OAuth
      window.location.href = supabaseOAuthUrl;
      
      return {};
    } catch (error) {
      console.error('Google sign in error:', error);
      return { error: 'Failed to initiate Google sign in' };
    }
  };

  const resetPassword = async (email: string): Promise<{ error?: string }> => {
    const rateLimitKey = `reset_${email}`;
    
    // Check rate limiting
    const rateLimitCheck = rateLimiter.isAllowed(rateLimitKey, 'passwordReset');
    if (!rateLimitCheck.allowed) {
      return { error: rateLimitCheck.error };
    }

    try {
      const response = await fetch(`${API_BASE}/auth/v1/recover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': ANON_KEY
        },
        body: JSON.stringify({
          email
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Parse error message first
        const errorMessage = data.msg || data.message || 'Password reset failed';
        
        // Record failed attempt
        rateLimiter.recordAttempt(rateLimitKey, 'passwordReset', false);
        
        // Log to centralized error handling system
        await handleApiError({
          message: errorMessage,
          status: response.status,
          statusText: response.statusText
        }, '/auth/v1/recover', 'password_reset', { 
          email,
          error_code: getAuthErrorCode('passwordReset', response, errorMessage),
          category: ErrorCategory.AUTHENTICATION
        });
        
        return { error: errorMessage };
      }

      // Record successful attempt
      rateLimiter.recordAttempt(rateLimitKey, 'passwordReset', true);
      return {};
    } catch (error) {
      console.error('Password reset error:', error);
      // Record failed attempt for network errors too
      rateLimiter.recordAttempt(rateLimitKey, 'passwordReset', false);
      
      // Log to centralized error handling system
      await handleApiError(error, '/auth/v1/recover', 'password_reset', { email });
      
      return { error: 'Network error during password reset' };
    }
  };

  const resendConfirmation = async (email: string): Promise<{ error?: string }> => {
    const rateLimitKey = `resend_${email}`;
    
    // Check rate limiting
    const rateLimitCheck = rateLimiter.isAllowed(rateLimitKey, 'resendConfirmation');
    if (!rateLimitCheck.allowed) {
      return { error: rateLimitCheck.error };
    }

    try {
      const response = await fetch(`${API_BASE}/auth/v1/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': ANON_KEY
        },
        body: JSON.stringify({
          email,
          type: 'signup'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Record failed attempt
        rateLimiter.recordAttempt(rateLimitKey, 'resendConfirmation', false);
        
        // Check for rate limiting from server
        if (response.status === 429) {
          return { error: 'Too many resend attempts. Please wait before trying again.' };
        }
        
        return { error: data.msg || data.message || 'Failed to resend confirmation email' };
      }

      // Record successful attempt
      rateLimiter.recordAttempt(rateLimitKey, 'resendConfirmation', true);
      return {};
    } catch (error) {
      console.error('Resend confirmation error:', error);
      // Record failed attempt for network errors too
      rateLimiter.recordAttempt(rateLimitKey, 'resendConfirmation', false);
      
      // Log to centralized error handling system
      await handleApiError(error, '/auth/v1/resend', 'resend_confirmation', { email });
      
      return { error: 'Network error while resending confirmation email' };
    }
  };

  // Session refresh functionality
  const refreshSession = async (): Promise<boolean> => {
    try {
      // Check localStorage first, then sessionStorage for refresh token
      let storedSession = localStorage.getItem('cycling-buddy-session');
      let storage = localStorage;
      
      if (!storedSession) {
        storedSession = sessionStorage.getItem('cycling-buddy-session');
        storage = sessionStorage;
      }
      
      if (!storedSession) {
        return false;
      }
      
      const session = JSON.parse(storedSession);
      if (!session.refresh_token) {
        return false;
      }
      
      const response = await fetch(`${API_BASE}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': ANON_KEY
        },
        body: JSON.stringify({
          refresh_token: session.refresh_token
        })
      });
      
      if (!response.ok) {
        // Refresh failed, session is truly expired
        storage.removeItem('cycling-buddy-session');
        setUser(null);
        return false;
      }
      
      const data = await response.json();
      
      // Update session with new tokens
      const newSession = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_at: getSessionExpiryTime(data.expires_in),
        user: data.user
      };
      
      storage.setItem('cycling-buddy-session', JSON.stringify(newSession));
      setUser(data.user);
      
      return true;
    } catch (error) {
      console.error('Session refresh failed:', error);
      return false;
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {

      // Check localStorage first, then sessionStorage
      let storedSession = localStorage.getItem('cycling-buddy-session');
      let storage = localStorage;
      let isPersistent = true;
      
      if (!storedSession) {
        storedSession = sessionStorage.getItem('cycling-buddy-session');
        storage = sessionStorage;
        isPersistent = false;
      }
      
      if (!storedSession) {
        return null;
      }
      
      const session = JSON.parse(storedSession);
      
      // If token is still valid, return it
      if (session.access_token && session.expires_at > Date.now()) {
        
        // Handle session-based authentication
        if (session.access_token === 'supabase-session-based') {
          // For session-based auth, we don't return a token but verify the session is still valid
          try {
            const response = await fetch(`${API_BASE}/auth/v1/user`, {
              headers: { 'apikey': ANON_KEY },
              credentials: 'include'
            });
            if (response.ok) {
              return 'supabase-session-based'; // Return identifier for session-based auth
            } else {
              // Session invalid
              storage.removeItem('cycling-buddy-session');
              setUser(null);
              return null;
            }
          } catch (error) {
            console.error('Session validation failed:', error);
            storage.removeItem('cycling-buddy-session');
            setUser(null);
            return null;
          }
        } else {
          return session.access_token;
        }
      }
      
      // Token is expired, try to refresh if we have refresh token
      if (session.refresh_token) {
        const refreshSuccessful = await refreshSession();
        if (refreshSuccessful) {
          // Get the refreshed token
          const refreshedSession = storage.getItem('cycling-buddy-session');
          if (refreshedSession) {
            const newSession = JSON.parse(refreshedSession);
            return newSession.access_token;
          }
        } else {
          // Refresh failed, show appropriate message based on "remember me" status
          if (isPersistent) {
            showInfo('Your session has expired. Please sign in again to continue.');
          } else {
            showInfo('Your session has expired. Please sign in again.');
          }
          
          // Clear expired session and show auth modal
          storage.removeItem('cycling-buddy-session');
          setUser(null);
          setShowAuthModalState(true);
        }
      } else {
        // No refresh token available
        if (isPersistent) {
          showInfo('Your session has expired. Please sign in again to continue.');
        } else {
          showInfo('Your session has expired. Please sign in again.');
        }
        
        // Clear expired session and show auth modal
        storage.removeItem('cycling-buddy-session');
        setUser(null);
        setShowAuthModalState(true);
      }
      
      return null;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      // First verify the current password by attempting to re-authenticate
      if (user?.email) {
        const verifyResponse = await fetch(`${API_BASE}/auth/v1/token?grant_type=password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': ANON_KEY
          },
          body: JSON.stringify({
            email: user.email,
            password: currentPassword
          })
        });

        if (!verifyResponse.ok) {
          throw new Error('Current password is incorrect');
        }
      }

      // Now update the password
      const response = await fetch(`${API_BASE}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': ANON_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          password: newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Regular password update failed:', { status: response.status, data });
        
        // Handle specific error cases
        if (data.message) {
          // Check for common password-related errors
          if (data.message.includes('same') || data.message.includes('current')) {
            throw new Error('You cannot use your current password. Please choose a different password.');
          } else if (data.message.includes('weak') || data.message.includes('strength')) {
            throw new Error('Password is too weak. Please choose a stronger password with a mix of letters, numbers, and symbols.');
          } else if (data.message.includes('common') || data.message.includes('breached')) {
            throw new Error('This password is too common. Please choose a more unique password.');
          } else if (data.message.includes('length') || data.message.includes('short')) {
            throw new Error('Password must be at least 8 characters long.');
          } else {
            throw new Error(data.message);
          }
        } else {
          throw new Error('Failed to update password. Please try again.');
        }
      }
    } catch (error: any) {
      console.error('Update password error:', error);
      
      // Log to centralized error handling system
      await handleApiError(error, '/auth/v1/user', 'update_password', { 
        user_id: user?.id 
      });
      
      // Don't double-wrap the error message
      throw error;
    }
  };

  const updatePasswordWithRecovery = async (newPassword: string): Promise<void> => {
    // Get recovery session instead of regular session
    const recoverySessionStr = localStorage.getItem('cycling-buddy-recovery-session');
    if (!recoverySessionStr) {
      throw new Error('No recovery session found');
    }

    const recoverySession = JSON.parse(recoverySessionStr);
    const token = recoverySession.access_token;

    if (!token) {
      throw new Error('No recovery token found');
    }

    try {
      // Update the password using the recovery token
      const response = await fetch(`${API_BASE}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': ANON_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          password: newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Password update failed:', { 
          status: response.status, 
          statusText: response.statusText,
          data,
          fullResponse: data,
          dataKeys: Object.keys(data || {}),
          dataValues: Object.values(data || {})
        });
        
        // Handle specific error cases - check all possible fields
        const errorMessage = data.message || data.error || data.error_description || 
                            data.msg || data.detail || data.details || 
                            JSON.stringify(data) || '';
   
        if (errorMessage) {
          // Check for common password-related errors (case insensitive)
          const lowerMessage = errorMessage.toLowerCase();
          if (lowerMessage.includes('same') || lowerMessage.includes('current') || lowerMessage.includes('identical')) {
            throw new Error('You cannot use your current password. Please choose a different password.');
          } else if (lowerMessage.includes('weak') || lowerMessage.includes('strength')) {
            throw new Error('Password is too weak. Please choose a stronger password with a mix of letters, numbers, and symbols.');
          } else if (lowerMessage.includes('common') || lowerMessage.includes('breached')) {
            throw new Error('This password is too common. Please choose a more unique password.');
          } else if (lowerMessage.includes('length') || lowerMessage.includes('short')) {
            throw new Error('Password must be at least 8 characters long.');
          } else {
            throw new Error(errorMessage);
          }
        } else {
          // If no error message found, create one based on status code
          if (response.status === 422) {
            throw new Error('Invalid password. This might be your current password or it doesn\'t meet the requirements. Please choose a different password.');
          } else {
            throw new Error('Failed to update password. Please try again.');
          }
        }
      }

      // Password update successful - now complete the login
      // Move recovery session to main session
      const mainSession = {
        access_token: recoverySession.access_token,
        refresh_token: recoverySession.refresh_token,
        expires_at: recoverySession.expires_at
      };
      
      localStorage.setItem('cycling-buddy-session', JSON.stringify(mainSession));
      localStorage.removeItem('cycling-buddy-recovery-session');
      
      // Complete authentication
      await checkSession();
      
      // Clear recovery state
      setIsInRecoveryMode(false);
      
    } catch (error: any) {
      console.error('Update password with recovery error:', error);
      
      // Log to centralized error handling system
      await handleApiError(error, '/auth/v1/user', 'update_password_recovery', {
        recovery_mode: true
      });
      
      // Don't double-wrap the error message since we already handled it above
      throw error;
    }
  };

  const cancelPasswordRecovery = async (): Promise<void> => {
    // Clear recovery session and log out
    localStorage.removeItem('cycling-buddy-recovery-session');
    localStorage.removeItem('cycling-buddy-session');
    setUser(null);
    setIsInRecoveryMode(false);
    setShowPasswordRecoveryForm(false);
  };

  const setShowAuthModal = useCallback((show: boolean, mode: 'signin' | 'signup' = 'signin', error: string | null = null) => {
    setShowAuthModalState(show);
    setAuthModalMode(mode);
    setAuthModalError(error);
    
    // Clear error when modal is closed
    if (!show) {
      setAuthModalError(null);
    }
  }, []);

  const deleteAccount = async (currentPassword: string): Promise<void> => {
    const token = await getAccessToken();
    if (!token || !user) {
      throw new Error('No authentication token or user found');
    }

    if (!currentPassword) {
      throw new Error('Current password is required for account deletion');
    }

    try {
      // First verify the password by attempting a sign in
      const verificationResponse = await fetch(`${API_BASE}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'apikey': ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: user.email,
          password: currentPassword
        })
      });

      if (!verificationResponse.ok) {
        throw new Error('Incorrect password. Please enter your current password to confirm account deletion.');
      }

      // If password verification succeeded, proceed with account deletion
      // Call the edge function to handle complete user deletion
      const response = await fetch(`${API_BASE}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'apikey': ANON_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user.id
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to delete account');
      }

      // Show success message before logging out
      showSuccess('Your account has been permanently deleted');

      // Use silent sign out since the user has been deleted from auth
      signOutSilent();
      
    } catch (error: any) {
      console.error('Delete account error:', error);
      
      // Log to centralized error handling system
      await handleApiError(error, '/functions/v1/delete-user', 'delete_account', { 
        user_id: user?.id 
      });
      
      throw new Error(error.message || 'Failed to delete account');
    }
  };

  // Development/testing utilities
  const forceExpireSession = isDevelopment ? () => {
    
    // Find and modify stored session
    let storedSession = localStorage.getItem('cycling-buddy-session');
    let storage = localStorage;
    
    if (!storedSession) {
      storedSession = sessionStorage.getItem('cycling-buddy-session');
      storage = sessionStorage;
    }
    
    if (storedSession) {
      const session = JSON.parse(storedSession);
      session.expires_at = Date.now() - 1000; // Expired 1 second ago
      storage.setItem('cycling-buddy-session', JSON.stringify(session));
    } 
  } : undefined;

  const getSessionInfo = isDevelopment ? () => {
    let storedSession = localStorage.getItem('cycling-buddy-session');
    let storageType: 'localStorage' | 'sessionStorage' | null = 'localStorage';
    
    if (!storedSession) {
      storedSession = sessionStorage.getItem('cycling-buddy-session');
      storageType = 'sessionStorage';
    }
    
    if (!storedSession) {
      return { expiresAt: 0, timeLeft: 0, storage: null };
    }
    
    const session = JSON.parse(storedSession);
    const now = Date.now();
    const expiresAt = session.expires_at || 0;
    const timeLeft = Math.max(0, expiresAt - now);
    
    return {
      expiresAt,
      timeLeft,
      storage: storageType
    };
  } : undefined;

  const value: AuthContextType = {
    user,
    loading,
    showPasswordRecoveryForm,
    setShowPasswordRecoveryForm,
    isInRecoveryMode,
    setIsInRecoveryMode,
    showAuthModal,
    authModalMode,
    authModalError,
    setShowAuthModal,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    resendConfirmation,
    updatePassword,
    updatePasswordWithRecovery,
    deleteAccount,
    checkSession,
    getAccessToken,
    refreshSession,
    cancelPasswordRecovery,
    // Development/testing utilities (only available in dev mode)
    ...(isDevelopment && {
      forceExpireSession,
      getSessionInfo
    })
  };

  // Proactively refresh token on app start if it expires soon
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      if (hasRunProactiveRefresh.current) {
        return; // Already ran in this session
      }
      
      try {
        // Check localStorage first, then sessionStorage
        let storedSession = localStorage.getItem('cycling-buddy-session');
        
        if (!storedSession) {
          storedSession = sessionStorage.getItem('cycling-buddy-session');
        }
        
        if (!storedSession) {
          return; // No session to refresh
        }
        
        const session = JSON.parse(storedSession);
        
        // Refresh if token expires within 5 minutes (300000ms)
        const REFRESH_THRESHOLD = 5 * 60 * 1000;
        const timeUntilExpiry = session.expires_at - Date.now();
        
        if (timeUntilExpiry <= REFRESH_THRESHOLD && session.refresh_token) {
          hasRunProactiveRefresh.current = true; // Mark as run to prevent infinite loop
          await refreshSession();

        } else if (timeUntilExpiry > REFRESH_THRESHOLD) {
          hasRunProactiveRefresh.current = true; // Mark as checked
        }
      } catch (error) {
        console.error('Error during proactive token refresh:', error);
        hasRunProactiveRefresh.current = true; // Mark as attempted to prevent infinite retry
      }
    };
    
    // Only run if user is logged in and we're not already loading
    if (user && !loading) {
      checkAndRefreshToken();
    }
  }, [user, loading]); // Removed refreshSession from dependencies

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
